import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import {
  getStates,
  getCities,
  getAccounts,
  getAllSurgeryCenters,
  getLabs,
  getLocationsByAccount,
  getPhysicians,
  getClients,
  checkClientExists,
  getSources,
  getSubsites,
  getSourcesWithSubsites,
  getIcdCodes,
  getProcedures,
  getInsuranceProviders,
  getInsurancePlanTypes,
  getPlanTypesByProvider,
  getPathologists,
  getBillingScenarios,
  getAccessioningTypes,
  getFacilities,
  getDefaultFacility,
  getGrossCodes,
  getAccessioningGrossCodes,
  getMatchingData,
  getCollectionMethods,
  StateOption,
  CityOption,
  SelectOption,
  IcdCodeOption,
  SelectOptionWithDescription,
  InsuranceProviderOption,
  PathologistOption,
  FacilityOption,
  InsurancePlanType,
  MatchingData,
  SourceOption,
} from "../services/referenceData.service";

// === US GEOGRAPHIC DATA ===
export function useStates(
  options?: Omit<UseQueryOptions<StateOption[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["states"],
    queryFn: getStates,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours - states rarely change
    ...options,
  });
}

export function useCities(
  stateCode: string | null | undefined,
  options?: Omit<UseQueryOptions<CityOption[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["cities", stateCode],
    queryFn: () => getCities(stateCode!),
    enabled: !!stateCode,
    staleTime: 1000 * 60 * 60, // 1 hour
    ...options,
  });
}

// === ACCOUNTS, LOCATIONS, PHYSICIANS ===
export function useAccounts(
  options?: Omit<UseQueryOptions<SelectOption[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["accounts"],
    queryFn: getAccounts,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
}

export const surgeryCentersQueryKey = ["surgery-centers"] as const;
export const labsQueryKey = ["labs"] as const;

export function useSurgeryCenters(
  options?: Omit<UseQueryOptions<SelectOption[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: surgeryCentersQueryKey,
    queryFn: getAllSurgeryCenters,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
}

export function useLabs(
  options?: Omit<UseQueryOptions<SelectOption[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: labsQueryKey,
    queryFn: getLabs,
    staleTime: 1000 * 60 * 5,
    ...options,
  });
}

export function useLocationsByAccount(
  accountId: string | null | undefined,
  options?: Omit<UseQueryOptions<SelectOption[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["locations", accountId],
    queryFn: () => getLocationsByAccount(accountId!),
    enabled: !!accountId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
}

export function usePhysicians(
  options?: Omit<UseQueryOptions<SelectOption[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["physicians"],
    queryFn: getPhysicians,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
}

// === CLIENTS ===
export function useClients(
  options?: Omit<UseQueryOptions<SelectOption[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["clients"],
    queryFn: getClients,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
}

export function useCheckClientExists(
  accountId: string | null | undefined,
  locationId: string | null | undefined,
  physicianId: string | null | undefined,
  options?: Omit<UseQueryOptions<boolean>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["client-check", accountId, locationId, physicianId],
    queryFn: () =>
      checkClientExists(accountId!, locationId || undefined, physicianId!),
    enabled: !!accountId && !!physicianId,
    ...options,
  });
}

// === SOURCES & SUBSITES ===
export function useSources(
  options?: Omit<UseQueryOptions<SourceOption[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["sources"],
    queryFn: getSources,
    staleTime: 1000 * 60 * 30, // 30 minutes
    ...options,
  });
}

export function useSubsites(
  sourceId: string | null | undefined,
  options?: Omit<UseQueryOptions<SelectOption[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["subsites", sourceId],
    queryFn: () => getSubsites(sourceId!),
    enabled: !!sourceId,
    staleTime: 1000 * 60 * 30, // 30 minutes
    ...options,
  });
}

export function useSourcesWithSubsites(
  options?: Omit<
    UseQueryOptions<{
      sources: SourceOption[];
      subsitesBySource: Record<string, SelectOption[]>;
    }>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery({
    queryKey: ["sources-with-subsites"],
    queryFn: getSourcesWithSubsites,
    staleTime: 1000 * 60 * 30, // 30 minutes
    ...options,
  });
}

// === ICD CODES ===
export function useIcdCodes(
  options?: Omit<UseQueryOptions<IcdCodeOption[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["icd-codes"],
    queryFn: getIcdCodes,
    staleTime: 1000 * 60 * 60, // 1 hour
    ...options,
  });
}

// === PROCEDURES ===
export function useProcedures(
  options?: Omit<UseQueryOptions<SelectOption[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["procedures"],
    queryFn: getProcedures,
    staleTime: 1000 * 60 * 30, // 30 minutes
    ...options,
  });
}

// === INSURANCE ===
export function useInsuranceProviders(
  options?: Omit<
    UseQueryOptions<InsuranceProviderOption[]>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery({
    queryKey: ["insurance-providers"],
    queryFn: getInsuranceProviders,
    staleTime: 1000 * 60 * 30, // 30 minutes
    ...options,
  });
}

export function useInsurancePlanTypes(
  options?: Omit<UseQueryOptions<InsurancePlanType[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["insurance-plan-types"],
    queryFn: getInsurancePlanTypes,
    staleTime: 1000 * 60 * 60, // 1 hour
    ...options,
  });
}

/**
 * Fetch plan types allowed for a specific insurance provider
 * Used to filter plan type dropdown based on selected provider
 * Only enabled when providerId is provided
 */
export function useInsurancePlanTypesByProvider(
  providerId: string | null | undefined,
  options?: Omit<UseQueryOptions<InsurancePlanType[]>, "queryKey" | "queryFn">
) {
  const isUuid =
    !!providerId &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      providerId
    );
  return useQuery({
    queryKey: ["insurance-plan-types-by-provider", providerId],
    queryFn: () => getPlanTypesByProvider(providerId!),
    enabled: isUuid && options?.enabled !== false,
    staleTime: 1000 * 60 * 30, // 30 minutes
    ...options,
  });
}

// === PATHOLOGISTS ===
export function usePathologists(
  options?: Omit<UseQueryOptions<PathologistOption[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["pathologists"],
    queryFn: getPathologists,
    staleTime: 1000 * 60 * 15, // 15 minutes
    ...options,
  });
}

// === BILLING SCENARIOS ===
export function useBillingScenarios(
  options?: Omit<UseQueryOptions<SelectOption[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["billing-scenarios"],
    queryFn: getBillingScenarios,
    staleTime: 1000 * 60 * 30, // 30 minutes
    ...options,
  });
}

// === ACCESSIONING TYPES ===
export function useAccessioningTypes(
  options?: Omit<UseQueryOptions<SelectOption[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["accessioning-types"],
    queryFn: getAccessioningTypes,
    staleTime: 1000 * 60 * 60, // 1 hour - rarely changes
    ...options,
  });
}

// === FACILITIES ===
export function useFacilities(
  options?: Omit<UseQueryOptions<FacilityOption[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["facilities"],
    queryFn: getFacilities,
    staleTime: 1000 * 60 * 60, // 1 hour
    ...options,
  });
}

export function useDefaultFacility(
  options?: Omit<UseQueryOptions<FacilityOption | null>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["default-facility"],
    queryFn: getDefaultFacility,
    staleTime: 1000 * 60 * 60, // 1 hour
    ...options,
  });
}

// === GROSS CODES ===
export function useGrossCodes(
  options?: Omit<
    UseQueryOptions<SelectOptionWithDescription[]>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery({
    queryKey: ["gross-codes"],
    queryFn: getGrossCodes,
    staleTime: 1000 * 60 * 30, // 30 minutes
    ...options,
  });
}

export function useAccessioningGrossCodes(
  options?: Omit<
    UseQueryOptions<SelectOptionWithDescription[]>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery({
    queryKey: ["accessioning-gross-codes"],
    queryFn: getAccessioningGrossCodes,
    staleTime: 1000 * 60 * 30, // 30 minutes
    ...options,
  });
}

export function useMatchingData(
  options?: Omit<UseQueryOptions<MatchingData>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["matching-data"],
    queryFn: getMatchingData,
    staleTime: 1000 * 60 * 5, // 5 minutes - matching data should be fresh
    ...options,
  });
}

// === COLLECTION METHODS ===
export function useCollectionMethods(
  options?: Omit<UseQueryOptions<SelectOption[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["collection-methods"],
    queryFn: getCollectionMethods,
    staleTime: 1000 * 60 * 30, // 30 minutes
    ...options,
  });
}
